/**
 * useInquiryLogs — Inquiry Agent 日志的加载 / 订阅 / fallback 合一 Hook
 *
 * 职责：
 *  1. 首屏 REST 拉取历史日志（`getInquiryLogsGet`）
 *  2. 订阅 IM `INQUIRY_LOG_ADDED` / `INQUIRY_LOG_UPDATED`
 *     - inquiryId 过滤后，直接用推送中的 inquiryId 调 REST 刷新
 *  3. fallback 轮询：IM 静默超过 `pollingFallbackInterval` 时触发一次 REST 回拉
 *  4. `inquiryId` 切换：清空 state 后重新走首屏
 *  5. `enabled=false`：仅保留首屏，关闭订阅与 fallback
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRequest, useInterval } from 'ahooks';
import { getInquiryLogsGet } from '@/services/swagger/easourcing/InquiryService';
import { usePushMessage } from '@/hooks/useWebSocket';
import { PushEventType } from '@/services/websocket/BDSocketManager';

import type { IIMInquiryLogPayload, InquiryLogSubStepLike, UseInquiryLogsOptions, UseInquiryLogsResult } from './types';
import { logIdentity } from './utils';

const DEFAULT_FALLBACK_INTERVAL = 30_000;

/**
 * Hook 主体。外部传入 `inquiryId` 与可选配置。
 */
export function useInquiryLogs(inquiryId: string, options: UseInquiryLogsOptions = {}): UseInquiryLogsResult {
  const {
    enabled = true,
    pollingFallbackInterval = DEFAULT_FALLBACK_INTERVAL,
    disablePollingFallback = false,
    order = 'asc',
  } = options;

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const [list, setList] = useState<InquiryLogSubStepLike[]>([]);

  /** 最近一次收到 IM / REST 的时间戳，用作 fallback 触发判断 */
  const lastImAtRef = useRef<number>(0);

  const orderRef = useRef(order);
  orderRef.current = order;

  // -------------------------------------------------------------------------
  // REST — 首屏 / 手动 refresh / fallback 轮询触发
  // -------------------------------------------------------------------------

  const {
    loading,
    error,
    refresh: runRefresh,
  } = useRequest(() => getInquiryLogsGet({ inquiryId }), {
    ready: !!inquiryId,
    refreshDeps: [inquiryId],
    onSuccess: resp => {
      const items = (resp?.data ?? []) as InquiryLogSubStepLike[];
      setList(items);
      lastImAtRef.current = Date.now();
    },
  });

  // -------------------------------------------------------------------------
  // inquiryId 切换：重置内部状态
  // -------------------------------------------------------------------------

  useEffect(() => {
    setList([]);
    lastImAtRef.current = 0;
  }, [inquiryId]);

  // -------------------------------------------------------------------------
  // 直接用推送中的 inquiryId 调 REST，绕开 useRequest 闭包
  // -------------------------------------------------------------------------

  const fetchLogsByPushId = useCallback(
    (pushInquiryId: string) => {
      getInquiryLogsGet({ inquiryId: pushInquiryId }).then(resp => {
        const items = (resp?.data ?? []) as InquiryLogSubStepLike[];
        setList(items);
        lastImAtRef.current = Date.now();
      });
    },
    [] // 无依赖，函数体内全靠参数传值
  );

  // -------------------------------------------------------------------------
  // IM 订阅 — ADDED / UPDATED → 直接用推送 inquiryId 刷新
  // -------------------------------------------------------------------------

  const handleLogPush = useCallback(
    (msg: { content: IIMInquiryLogPayload }) => {
      const payload = msg?.content;
      if (!payload) return;

      const pushId = String(payload.inquiryId);
      if (pushId !== inquiryId) return;

      lastImAtRef.current = Date.now();
      fetchLogsByPushId(pushId);
    },
    [inquiryId, fetchLogsByPushId]
  );

  // usePushMessage 的 handler 参数类型宽泛为 Record<string, unknown> | void；
  // 这里用 as-unknown-cast 桥接到我们的强类型 handler。
  const addedState = usePushMessage<IIMInquiryLogPayload>(
    PushEventType.INQUIRY_LOG_ADDED,
    handleLogPush as unknown as Parameters<typeof usePushMessage<IIMInquiryLogPayload>>[1]
  );
  usePushMessage<IIMInquiryLogPayload>(
    PushEventType.INQUIRY_LOG_UPDATED,
    handleLogPush as unknown as Parameters<typeof usePushMessage<IIMInquiryLogPayload>>[1]
  );

  // -------------------------------------------------------------------------
  // Fallback polling — 仅当 enabled 且未 disable，且 IM 静默
  // -------------------------------------------------------------------------

  const shouldPoll = enabled && !disablePollingFallback;

  useInterval(
    () => {
      if (!shouldPoll) return;
      const silentFor = Date.now() - lastImAtRef.current;
      if (silentFor < pollingFallbackInterval) return;
      // IM 静默超时 → 触发一次 REST 回拉
      runRefresh();
    },
    shouldPoll ? pollingFallbackInterval : undefined
  );

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  const refresh = useCallback(() => {
    runRefresh();
  }, [runRefresh]);

  // 作为调试辅助；addedState 的 isConnected 代表底层 WebSocket 是否已连
  const imConnected = Boolean(addedState?.isConnected);

  // 额外做一次最终去重（防御性）
  const data = useMemo(() => {
    const seen = new Set<string>();
    const out: InquiryLogSubStepLike[] = [];
    for (const item of list) {
      const id = logIdentity(item);
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(item);
    }
    return out;
  }, [list]);

  return {
    data,
    loading,
    error: error ?? null,
    refresh,
    imConnected,
  };
}

export default useInquiryLogs;
